import { saleStatusLabel, type SaleSharePayload } from '@klothiq/domain';
import { buildSaleReceiptPdf } from '@/utils/pdfTemplates';

/** Build from the same authorized, customer-facing snapshot as the message. */
export async function createSaleReceiptFile(
  sale: SaleSharePayload,
  includeAddress: boolean,
): Promise<File> {
  const pdf = buildSaleReceiptPdf(
    {
      sale_number: sale.sale_number,
      sale_date: sale.sale_date,
      status: sale.sale_status,
      payment_status: sale.payment_status,
      payment_method: saleStatusLabel(sale.payment_status),
      customer_name: sale.customer_name ?? 'Customer',
      customer: {
        name: sale.customer_name ?? 'Customer',
        phone: sale.customer_mobile,
        address: includeAddress ? sale.customer_address : null,
      },
      ...(sale.settings.include_seller ? { seller_name: sale.seller_name } : {}),
      sale_items: sale.items.map((item) => ({
        product_name:
          item.returned_qty > 0 ? `${item.name} (Returned: ${item.returned_qty})` : item.name,
        size: item.size ?? undefined,
        color: item.color,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.line_total,
      })),
      subtotal: sale.items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0),
      discount: sale.items.reduce((sum, item) => sum + item.discount, 0),
      delivery_charge: sale.delivery_charge,
      total: sale.total_amount,
      amount_paid: sale.financial_summary.net_collected,
      financial_summary: sale.financial_summary,
      return_status: sale.return_status,
    },
    sale.settings.business_name,
  );
  const reference = sale.sale_number.replace(/[^a-zA-Z0-9_-]/g, '_');
  return new File([pdf.output('blob')], `KLOTHIQ-${reference || 'Sale'}-${sale.sale_date}.pdf`, {
    type: 'application/pdf',
  });
}

export function canShareReceipt(file: File): boolean {
  try {
    return (
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    );
  } catch {
    return false;
  }
}
